import { useMemo } from "react";

import { useGetAllEmployeeWithVehicle } from "../api/employee";
import {
  TCompanyInvitation,
  useGetCompanyInvitations,
} from "../api/invitation";

/**
 * The collaborator roster: confirmed members AND outstanding invitations, in
 * one list.
 *
 * WHY
 * ---
 * The list used to be built from /companies/{id}/employees-vehicle alone, i.e.
 * rows in `company_user`. A `company_user` row only comes into existence when
 * the invitee finishes signing up (InvitationService::acceptInvitation). So
 * between "invitation sent" and "invitation accepted" the person existed
 * nowhere in the UI - a manager could not tell whether they had already
 * invited someone, whether the link had expired after its 72h, or whether it
 * simply never arrived. The only recourse was to invite again and hope.
 *
 * The invitation endpoints were already on the server, unused. This merges
 * them in.
 *
 * DEDUPLICATION
 * -------------
 * An accepted invitation and its resulting member are the same human, so they
 * must not appear twice. Members win: once someone is in `company_user` that
 * row is the authoritative record, and the invitation is only kept as history.
 * Matching is on lowercased email, the only field both sides reliably share -
 * `company_user.user_id` is not on the invitation, and names may be blank.
 */

export type RosterStatus =
  | "member" // in company_user - a real collaborator
  | "pending" // invited, link still valid
  | "expired" // invited, 72h window elapsed
  | "revoked"; // invitation cancelled by a manager

export interface RosterRow {
  /** Stable key. Members use their user id, invitations their code. */
  key: string;
  status: RosterStatus;
  firstname: string;
  lastname: string;
  email: string;
  department: string | null;
  brand: string | null;
  model: string | null;
  /** Present for members only - used to open the collaborator sheet. */
  userId: number | null;
  companyUserId: number | null;
  vehicleId: number | null;
  /** Present for invitations only - required to resend or revoke. */
  invitationCode: string | null;
  expiresAt: string | null;
  role: string | null;
}

const normaliseEmail = (value?: string | null) =>
  (value ?? "").trim().toLowerCase();

const invitationStatus = (inv: TCompanyInvitation): RosterStatus => {
  if (inv.status === "revoked") return "revoked";
  if (inv.status === "expired") return "expired";
  // Defensive: the API auto-expires in its response, but a clock skew or a
  // cached payload could still hand us a "pending" row that is past due.
  if (inv.expires_at && new Date(inv.expires_at).getTime() < Date.now()) {
    return "expired";
  }
  return "pending";
};

export const useCollaboratorRoster = (companyId: number) => {
  const { employees, isPending: employeesPending } =
    useGetAllEmployeeWithVehicle(companyId);
  const { invitations, isPending: invitationsPending } =
    useGetCompanyInvitations(companyId);

  const rows = useMemo<RosterRow[]>(() => {
    const memberRows: RosterRow[] = (employees ?? []).map((e: any) => ({
      key: `member-${e.companyUserId ?? e.userId}-${e.vehicleId ?? "novehicle"}`,
      status: "member",
      firstname: e.firstname ?? "",
      lastname: e.lastname ?? "",
      email: e.email ?? "",
      department: e.department ?? null,
      brand: e.brand ?? null,
      model: e.model ?? null,
      userId: e.userId ?? null,
      companyUserId: e.companyUserId ?? null,
      vehicleId: e.vehicleId ?? null,
      invitationCode: null,
      expiresAt: null,
      role: null,
    }));

    const memberEmails = new Set(
      (employees ?? [])
        .map((e: any) => normaliseEmail(e.email))
        .filter(Boolean)
    );

    const invitationRows: RosterRow[] = (invitations ?? [])
      .filter((inv) => {
        // An accepted invitation whose member row exists is just history.
        if (inv.status === "accepted") return false;
        // Belt and braces: also drop any invitation whose email already has a
        // member row, so a stale "pending" row cannot duplicate a person who
        // has in fact signed up.
        return !memberEmails.has(normaliseEmail(inv.company_email));
      })
      .map((inv) => ({
        key: `invitation-${inv.invitation_code}`,
        status: invitationStatus(inv),
        firstname: inv.firstname ?? "",
        lastname: inv.lastname ?? "",
        email: inv.company_email,
        department: null,
        brand: null,
        model: null,
        userId: null,
        companyUserId: null,
        vehicleId: null,
        invitationCode: inv.invitation_code,
        expiresAt: inv.expires_at,
        role: inv.company_role,
      }));

    // Outstanding invitations first: they are the rows that need an action.
    const order: Record<RosterStatus, number> = {
      pending: 0,
      expired: 1,
      revoked: 2,
      member: 3,
    };

    return [...invitationRows, ...memberRows].sort(
      (a, b) =>
        order[a.status] - order[b.status] ||
        `${a.lastname} ${a.firstname}`.localeCompare(
          `${b.lastname} ${b.firstname}`
        )
    );
  }, [employees, invitations]);

  return {
    rows,
    isPending: employeesPending || invitationsPending,
    memberCount: rows.filter((r) => r.status === "member").length,
    pendingCount: rows.filter((r) => r.status === "pending").length,
    expiredCount: rows.filter((r) => r.status === "expired").length,
  };
};
